/**
 * SD-1248: bringing an invitee back to their practice invitation after sign-in or sign-up.
 *
 * Signing in already comes back by itself: AuthGuard sends /practice-invite/:token to the login
 * page as ?redirectURL=, and the login page goes there afterwards. Signing up does not -- the
 * sign-up steps end on /dashboard (and email verification can happen in another tab) -- so the
 * invitation is also remembered here, in localStorage because sessionStorage is per tab and is
 * cleared on sign-out. ContentLayoutComponent resumes it on the first signed-in page; the
 * invitation page forgets it once reached. It lapses after a day so a link abandoned long ago
 * cannot hijack a later visit.
 */

const KEY = 'PendingPracticeInvite';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PREFIX = '/practice-invite/';

export class PracticeInviteReturn {

  /** True for an address this helper will remember. Nothing else may be stored or resumed. */
  static isInviteUrl(url: string | null | undefined): boolean {
    return typeof url === 'string' && url.indexOf(PREFIX) === 0 && url.length > PREFIX.length;
  }

  static remember(url: string): void {
    if (!PracticeInviteReturn.isInviteUrl(url)) {
      return;
    }
    try {
      localStorage.setItem(KEY, JSON.stringify({ url, at: Date.now() }));
    } catch {
      // Storage unavailable: the invitee can open the email link again.
    }
  }

  /** The remembered invitation, if one is waiting and still fresh. */
  static pending(now: number = Date.now()): string | null {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) {
        return null;
      }
      const value = JSON.parse(raw);
      if (!value || !PracticeInviteReturn.isInviteUrl(value.url) || !(now - value.at < MAX_AGE_MS)) {
        localStorage.removeItem(KEY);
        return null;
      }
      return value.url;
    } catch {
      return null;
    }
  }

  static forget(): void {
    try {
      localStorage.removeItem(KEY);
    } catch { }
  }
}
